import React, { useEffect, useMemo, useState } from "react";
import styles from "./users-and-roles.module.css";
import CrossIcon from "../Icons/cross-icon";
import MagnifyingGlassIcon from "../Icons/magnifying-glass-icon";
import { MODAL_ACTIONS, USER_ACTIONS } from "./UserInvitationModal";
import { EDIT_MODAL_ACTIONS, EDIT_USER_ACTIONS } from "./UserEditModal";
import { enterpriseLevelsDetailsSelector } from "../../Reducers/enterpriseLevelsDetailsReducer";
import { useSelector } from "react-redux";
import { CircleRadioIcon } from "../Accounts/AddAccountModal";
import { REGIONAL, SUB_REGIONAL } from "../../Enums/administratorLevels";
import { cx } from "../../Util/classHelpers";
import CustomSelect from "../CommonComponents/select/CustomSelect";

const Selection = ({orgType, onCancel, confirm, orgList, superOrg, extraControl = null}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);

  useEffect(() => {
    if (orgList.length === 1) {
      setSelectedOrg(orgList[0]);
    } else {
      setSelectedOrg(null);
    }
  }, [orgList]);

  const filteredOrgs = useMemo(() => {
    return orgList.filter(org => org.getName().toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, orgList]);

  const handleConfirm = () => {
    confirm(selectedOrg);
  };

  const orgTypeTitleCase = orgType.charAt(0).toUpperCase() + orgType.slice(1).toLowerCase();
  const orgTypePlural = orgTypeTitleCase + "s";
  const orgTypeLowercase = orgType.toLowerCase();

  const confirmDisabled = selectedOrg === null;

  return (<div className={styles.orgSelectorContainer}>
      <div className={styles.orgSelectorHeader}>
        <div className={styles.orgSelectorHeaderContent}>
          <div className={styles.orgSelectorTitle}>
            <h2 className={styles.orgSelectorTitleText}>Choose {orgTypeTitleCase}</h2>
            <p className={styles.orgSelectorDescription}>
              Select a {orgTypeLowercase} where this user will have access.
            </p>
          </div>
        </div>
        <button className={styles.orgSelectorCloseButton} onClick={onCancel}>
          <div className={styles.orgSelectorCloseIcon}>
            <CrossIcon />
          </div>
        </button>
      </div>

      <div className={styles.orgSelectorSearchSection}>
        <div className={styles.orgSelectorSearchWrapper}>
          <div>
            <MagnifyingGlassIcon />
          </div>
          <input
            type="text"
            placeholder={`Search ${orgTypeLowercase}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.orgSelectorSearchInput}
          />
        </div>
        {extraControl}
      </div>

      <div className={styles.orgSelectorContent}>
        <div className={styles.orgSelectorListHeader}>
          <span className={styles.orgSelectorCountryName}>{superOrg.getName()}</span>
          <span className={styles.orgSelectorAvailableCount}>
            {filteredOrgs.length} {orgTypePlural} available
          </span>
        </div>
        
        <div className={styles.orgSelectorList}>
          {filteredOrgs.map((org) => (
            <div key={org.getId()} className={styles.orgSelectorItem} onClick={() => setSelectedOrg(org)}>
              <CircleRadioIcon selected={selectedOrg?.getId() === org.getId()} />
              <span className={styles.orgSelectorItemText}>{org.getName()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.orgSelectorFooter}>
        <button 
          className={styles.orgSelectorCancelButton}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          className={cx(styles.orgSelectorConfirmButton, confirmDisabled && styles.disabled)}
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          Confirm
        </button>
      </div>
    </div>);
}

export const RegionSelection = ({ onClose, user, modalDispatch, usersDispatch, isEditMode = false }) => {
  const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);

  const handleConfirm = (region) => {
    if (isEditMode) {
      usersDispatch({ type: EDIT_USER_ACTIONS.UPDATE_USER, payload: { ...user, accessId: region.getId(), administratorLevel: REGIONAL } });
      modalDispatch({ type: EDIT_MODAL_ACTIONS.EDIT_FORM });
    } else {
      usersDispatch({ type: USER_ACTIONS.UPDATE_USER, payload: { id: user.id, user: { ...user, accessId: region.getId(), administratorLevel: REGIONAL } } });
      modalDispatch({ type: MODAL_ACTIONS.INVITE_FORM });
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      modalDispatch({ type: EDIT_MODAL_ACTIONS.EDIT_FORM });
    } else {
      modalDispatch({ type: MODAL_ACTIONS.INVITE_FORM });
    }
  };

  return (
    <Selection 
      orgType="region"  
      onCancel={handleCancel} 
      confirm={handleConfirm} 
      orgList={enterpriseLevelsDetails.getRegions()} 
      superOrg={enterpriseLevelsDetails.getEnterprise()}
    />
  );
};

export const SubregionSelection = ({ onClose, user, modalDispatch, usersDispatch, isEditMode = false }) => {
  const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);
  const [selectedRegionId, setSelectedRegionId] = useState(enterpriseLevelsDetails.getRegions()[0].getId());

  const handleConfirm = (subregion) => {
    if (isEditMode) {
      usersDispatch({ type: EDIT_USER_ACTIONS.UPDATE_USER, payload: { ...user, accessId: subregion.getId(), administratorLevel: SUB_REGIONAL } });
      modalDispatch({ type: EDIT_MODAL_ACTIONS.EDIT_FORM });
    } else {
      usersDispatch({ type: USER_ACTIONS.UPDATE_USER, payload: { id: user.id, user: { ...user, accessId: subregion.getId(), administratorLevel: SUB_REGIONAL } } });
      modalDispatch({ type: MODAL_ACTIONS.INVITE_FORM });
    }
  }

  const handleCancel = () => {
    if (isEditMode) {
      modalDispatch({ type: EDIT_MODAL_ACTIONS.EDIT_FORM });
    } else {
      modalDispatch({ type: MODAL_ACTIONS.INVITE_FORM });
    }
  };

  const extraControl = <CustomSelect 
    options={enterpriseLevelsDetails.getRegions().map(region => ({ label: region.getName(), value: region.getId() }))}
    selected={selectedRegionId}
    onSelect={(value) => {setSelectedRegionId(value);}}
    selectClassName={styles.orgSelectorCustomSelect}
  />

  const orgList = useMemo(() => {
    return enterpriseLevelsDetails.getSubregionsFromRegionId(selectedRegionId);
  }, [selectedRegionId, enterpriseLevelsDetails]);
  
  return <Selection 
    orgType="subregion" 
    onCancel={handleCancel} 
    confirm={handleConfirm} 
    orgList={orgList} 
    superOrg={enterpriseLevelsDetails.getRegions().find(region => region.getId() === selectedRegionId)}
    extraControl={extraControl}
  />
}
